/*----------------------------------
- TLD PAGE: NAME SAMPLE
----------------------------------*/

/**
 * "10 one-word names" on the kit `Card` and `Table` (the mock's sample card, website-tld-insight.html): the name, its
 * status as a kit chip and its price, in the listing's own order.
 *
 * @docs docs/features/tld-intelligence
 * @fix  docs/fixes/2026-10-17-tld-sample-nan-price.md
 * @rule One bounded sample (ADR-0028 amendment): at most `TLD_INSIGHT_SAMPLE_LIMIT` names, in the listing's own order
 *       (the engine's), with three fields (name, status, price) and nothing else; the table never sorts, filters or
 *       pages them for a visitor.
 */

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import Card from '@uniquedomains/components/kit/card/Card';
import Chip from '@uniquedomains/components/kit/chip/Chip';
import Name from '@uniquedomains/components/kit/name/Name';
import Price from '@uniquedomains/components/kit/price/Price';
import Table from '@uniquedomains/components/kit/table/Table';
import type { TableColumn, TableRow } from '@uniquedomains/components/kit/table/tableTypes';
import { formatTldUsdOr } from '@uniquedomains/components/contracts/tlds/format';
import { TLD_INSIGHT_SAMPLE_LIMIT, formatTldLabel, type PublicTldSampleRow } from '@uniquedomains/components/contracts/tlds/public';
import { describeTldNameStatus } from '@uniquedomains/components/tlds/page/pageModel';

import { TLD_PAGE_CONTENT } from '@/client/catalogs/landing/tld/pageContent';

/*----------------------------------
- HELPERS
----------------------------------*/

/**
 * The mock's `tl-c-st` 110px and `tl-c-pr` 90px tracks (web-market-26): the status and the price as two narrow columns at
 * the right, so the chip sits beside its price; the name takes the rest. A `colgroup` (the kit's `tracks`), so the fixed
 * layout keeps these widths with its head row out of sight (`tldPage.css` `.tl-sample`, review R3-T T-7).
 */
const SAMPLE_TRACKS = { status: '110px', price: '90px' } as const;

/** `harbor.ai` as its word: the sample's names all end in the page's TLD. */
const wordOf = (domain: string, slug: string): string => (domain.endsWith(`.${slug}`) ? domain.slice(0, -(slug.length + 1)) : domain);

const toRow = (row: PublicTldSampleRow, slug: string, content: typeof TLD_PAGE_CONTENT.insight.sample): TableRow => {
    const status = describeTldNameStatus(row.status);
    const price = formatTldUsdOr(row.price, content.noPrice);
    return {
        id: row.domain,
        cells: {
            name: <Name word={wordOf(row.domain, slug)} tld={slug} truncate data-testid="tld-page-sample-row" />,
            status: status ? <Chip tone={status.tone} label={status.label} /> : null,
            price: price === content.noPrice ? <span class="tl-cap">{content.noPrice}</span> : <Price value={price} />,
        },
    };
};

/*----------------------------------
- COMPONENT
----------------------------------*/

export default function TldSampleSection({ slug, sample }: { slug: string; sample: readonly PublicTldSampleRow[] }) {
    const content = TLD_PAGE_CONTENT.insight.sample;
    // Product already caps it; slicing again keeps the page to its contract whatever arrives.
    const rows = sample.slice(0, TLD_INSIGHT_SAMPLE_LIMIT);
    const cols: TableColumn[] = [
        { key: 'name', label: content.domain },
        { key: 'status', label: content.status },
        { key: 'price', label: content.price, align: 'right' },
    ];

    return (
        <Card head={{ title: content.heading }} data-testid="tld-page-sample">
            {rows.length === 0 ? <p class="tl-cardp">{content.empty}</p> : (
                // No pin and no phone rows: three short columns fit a phone, and the sample is read, never decided.
                <Table class="tl-sample" variant="list" pin={false} phone={false} tracks={SAMPLE_TRACKS} cols={cols} rows={rows.map((row) => toRow(row, slug, content))} caption={content.caption(formatTldLabel(slug))} />
            )}
        </Card>
    );
}
